import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Controller, useForm } from 'react-hook-form'
import { Box } from '@material-ui/core'
import noop from 'lodash/noop'
import { useHistory } from 'react-router-dom'
import Checkbox from '../../../../../molecules/Checkbox'
import SydInput from '../../../../../commonDesign/SydInput'
import SydLabel, { hookFormErrorAdapter } from '../../../../../commonDesign/SydLabel'
import Loading from '../../../../../molecules/Loading'
import SydSelect from '../../../../../commonDesign/SydSelect'
import SydButton from '../../../../../commonDesign/Button'
import { useCheckPolicy } from '../../../../../../hooks'
import { ADMIN_ROUTES } from '../../../../../../constants'
import { useListInfographicBlocks } from '../../hooks/useListInfographicBlocks'
import {
  useSaveInfographicBlockMutation
} from '../../InfographicBlocksDetailsView/hooks/useSaveInfographicBlockMutation'
import Alert from '../../../../../atoms/Alert'

export const CreateInfographicBlockForm = ({
  onCancel = noop
}) => {
  const userIsInternal = useCheckPolicy('internalOnly')
  const history = useHistory()
  const form = useForm({
    defaultValues: {
      duplicateInfographicBlockId: ' ',
      global: true,
      internal: false,
      ordinal: 99999,
      name: '',
      description: ''
    }
  })

  const { data: infographicBlocks, isFetching } = useListInfographicBlocks({
    sort: [{ id: 'ordinal', desc: false }]
  })
  const infographicBlockOptions = useMemo(() => {
    if (!infographicBlocks) return []
    return [
      { value: ' ', label: '-- Start from scratch --' },
      ...infographicBlocks.map(infographicBlock => ({
        value: infographicBlock.infographicBlockId,
        label: infographicBlock.name
      }))
    ]
  }, [infographicBlocks])

  const { mutateAsync: saveMutateAsync, isLoading: isLoadingSave, isError: isErrorSave } = useSaveInfographicBlockMutation()
  const onSubmit = async (data) => {
    const saveResponse = await saveMutateAsync({
      ...data,
      duplicateInfographicBlockId: isNaN(Number(data.duplicateInfographicBlockId)) ? undefined : Number(data.duplicateInfographicBlockId),
      ordinal: Math.min(data.ordinal, 99999)
    })
    try {
      const body = JSON.parse(saveResponse.body)
      if (body?.infographicBlockId) {
        history.push(`${ADMIN_ROUTES.INFOGRAPHIC_REPORTS_BLOCK}/${body.infographicBlockId}`)
      }
    } catch (e) {
      console.error(e)
    }
  }

  return (
    <form {...form} onSubmit={form.handleSubmit(onSubmit)}>
      <Box display='flex' flexDirection='column' gridGap='10px'>
        {isFetching ? (
          <Box flex={1} alignItems='center' justifyContent='center'>
            <Loading />
          </Box>
        ) : (
          <>
            {isErrorSave && (
              <Alert severity='error'>Error saving infographic block</Alert>
            )}
            <Controller
              control={form.control}
              name='duplicateInfographicBlockId'
              render={({ field, fieldState }) => (
                <SydLabel label="Start from another infographic block's content" error={hookFormErrorAdapter(form, fieldState)} required>
                  <SydSelect
                    options={infographicBlockOptions}
                    size='sm'
                    {...field}
                    minWidth='200px'
                  />
                </SydLabel>
              )}
            />

            <Controller
              control={form.control}
              name='name'
              rules={{ required: { value: true, message: 'Name required' } }}
              render={({ field, fieldState }) => (
                <SydLabel label='Infographic Block Name' error={hookFormErrorAdapter(form, fieldState)} required>
                  <SydInput
                    size='sm'
                    {...field}
                  />
                </SydLabel>
              )}
            />

            <Controller
              control={form.control}
              name='description'
              render={({ field, fieldState }) => (
                <SydLabel label='Description' error={hookFormErrorAdapter(form, fieldState)}>
                  <SydInput
                    size='sm'
                    {...field}
                  />
                </SydLabel>
              )}
            />

            <Box display='flex' gridGap='8px' flexWrap='wrap' justifyContent='space-between'>
              <Controller
                control={form.control}
                name='ordinal'
                render={({ field, fieldState }) => (
                  <SydLabel label='Ordinal' error={hookFormErrorAdapter(form, fieldState)} required>
                    <SydInput
                      size='sm'
                      type='number'
                      {...field}
                      defaultValue={99999}
                      inputProps={{ min: 1, max: 99999 }}
                    />
                  </SydLabel>
                )}
              />
            </Box>

            {userIsInternal && (
              <Controller
                control={form.control}
                name='internal'
                render={({ field, fieldState }) => (
                  <Box display='flex' gridGap={4} mt={2}>
                    <Checkbox
                      checked={!!field.value}
                      onChange={(e, checked) => field.onChange(checked)}
                    />
                    <Box onClick={() => field.onChange(!field.value)}>
                      <SydLabel
                        label='Internal Only?'
                        description='Will only be visible to internal users'
                      />
                    </Box>
                  </Box>
                )}
              />
            )}

            <Box display='flex' justifyContent='space-between' mt={2}>
              <SydButton
                type='button'
                variant='outline'
                onClick={() => onCancel()}
              >
                Cancel
              </SydButton>

              <SydButton
                type='submit'
                variant='primary'
                processing={isLoadingSave}
              >
                Save
              </SydButton>
            </Box>
          </>
        )}
      </Box>
    </form>
  )
}

CreateInfographicBlockForm.propTypes = {
  onCancel: PropTypes.func
}
